import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MonitorSmartphone, Save } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { Badge, Button, Card, CardHeader, Checkbox, ErrorState, Field, Input, LoadingBlock, useToast } from './ui';

/**
 * Settings › Attendance › Desktop agent & idle policy (the agent CR's §30, §48).
 * Every value is a setting, never code; the server checks them together before
 * anything is saved (§31), and a refusal is shown on the field it is about.
 */

const KEYS = {
  required: 'attendance.agentRequired',
  heartbeat: 'attendance.agentHeartbeatSeconds',
  freshness: 'attendance.agentFreshnessSeconds',
  grace: 'attendance.agentOfflineGraceMinutes',
  idleEnabled: 'attendance.idleDetectionEnabled',
  idle: 'attendance.idleThresholdMinutes',
  penaltyEnabled: 'attendance.idlePenaltyEnabled',
  penalty: 'attendance.idlePenaltyThresholdMinutes',
  days: 'attendance.idlePenaltyDays',
  max: 'attendance.idlePenaltyMaxDaysPerDay',
  approval: 'attendance.idlePenaltyRequiresApproval',
  download: 'attendance.agentDownloadUrl',
  downloadMac: 'attendance.agentDownloadUrlMac',
} as const;

interface Problem {
  key: string;
  reason: string;
  limit?: number;
}

export function AgentPolicyPanel() {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [problems, setProblems] = useState<Problem[]>([]);

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });

  useEffect(() => {
    setDraft({});
    setProblems([]);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => api.put('/settings', { values: draft }),
    onSuccess: () => {
      toast.success(t('settings.saved'));
      setDraft({});
      setProblems([]);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'me'] });
    },
    onError: (e: Error) => {
      const found = e instanceof ApiError ? ((e.details as { problems?: Problem[] } | undefined)?.problems ?? []) : [];
      setProblems(found);
      toast.error(found.length ? t('settings.agentFix') : e.message);
    },
  });

  if (settings.isLoading) return <LoadingBlock />;
  if (settings.error) return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;

  const current = (key: string): unknown => (key in draft ? draft[key] : settings.data?.attendance?.[key]);
  const set = (key: string, value: unknown) => setDraft((d) => ({ ...d, [key]: value }));
  const dirty = Object.keys(draft).length > 0;

  const problemFor = (key: string): string | null => {
    const p = problems.find((x) => x.key === key);
    if (!p) return null;
    return t(`settings.problem.${p.reason}` as TranslationKey, { limit: p.limit ?? '' });
  };

  const number = (key: string, label: TranslationKey, hint: TranslationKey | null, unit: TranslationKey, opts: { step?: number; min?: number; disabled?: boolean } = {}) => (
    <Field
      label={
        <span className="flex items-center gap-2">
          {t(label)}
          {key in draft && <Badge tone="warning">{t('settings.unsaved')}</Badge>}
        </span>
      }
      hint={
        <>
          {hint && <span className="block">{t(hint)}</span>}
          <span className="font-mono text-mono-sm" dir="ltr">{key}</span>
        </>
      }
      error={problemFor(key)}
    >
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={opts.min ?? 0}
          step={opts.step ?? 1}
          disabled={opts.disabled}
          className="max-w-[9rem] tabular"
          value={String(current(key) ?? '')}
          onChange={(e) => set(key, e.target.value === '' ? '' : Number(e.target.value))}
        />
        <span className="text-body-sm text-muted">{t(unit)}</span>
      </div>
    </Field>
  );

  const toggle = (key: string, label: TranslationKey, hint: TranslationKey | null) => (
    <Field
      label={
        <span className="flex items-center gap-2">
          {t(label)}
          {key in draft && <Badge tone="warning">{t('settings.unsaved')}</Badge>}
        </span>
      }
      hint={
        <>
          {hint && <span className="block">{t(hint)}</span>}
          <span className="font-mono text-mono-sm" dir="ltr">{key}</span>
        </>
      }
      error={problemFor(key)}
    >
      <Checkbox
        checked={Boolean(current(key))}
        onChange={(e) => set(key, e.target.checked)}
        label={current(key) ? t('common.yes') : t('common.no')}
      />
    </Field>
  );

  const link = (key: string, label: TranslationKey, hint: TranslationKey) => (
    <Field
      label={
        <span className="flex items-center gap-2">
          {t(label)}
          {key in draft && <Badge tone="warning">{t('settings.unsaved')}</Badge>}
        </span>
      }
      hint={
        <>
          <span className="block">{t(hint)}</span>
          <span className="font-mono text-mono-sm" dir="ltr">{key}</span>
        </>
      }
      error={problemFor(key)}
    >
      <Input type="url" dir="ltr" placeholder="https://" value={String(current(key) ?? '')} onChange={(e) => set(key, e.target.value)} />
    </Field>
  );

  const idleOn = Boolean(current(KEYS.idleEnabled));
  const penaltyOn = Boolean(current(KEYS.penaltyEnabled));

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <MonitorSmartphone className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
            {t('settings.agentTitle')}
          </span>
        }
        subtitle={t('settings.agentNote')}
        action={
          <Button variant="primary" icon={<Save className="h-4 w-4" />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        }
      />

      <Section title={t('settings.agentSection')}>
        {toggle(KEYS.required, 'settings.agentRequired', 'settings.agentRequiredHint')}
        {link(KEYS.download, 'settings.agentDownload', 'settings.agentDownloadHint')}
        {link(KEYS.downloadMac, 'settings.agentDownloadMac', 'settings.agentDownloadMacHint')}
        {number(KEYS.heartbeat, 'settings.agentHeartbeat', 'settings.agentHeartbeatHint', 'settings.unitSeconds', { min: 5 })}
        {number(KEYS.freshness, 'settings.agentFreshness', 'settings.agentFreshnessHint', 'settings.unitSeconds', { min: 5 })}
        {number(KEYS.grace, 'settings.agentGrace', 'settings.agentGraceHint', 'settings.unitMinutes')}
      </Section>

      <Section title={t('settings.idleSection')}>
        {toggle(KEYS.idleEnabled, 'settings.idleEnabled', 'settings.idleEnabledHint')}
        {number(KEYS.idle, 'settings.idleThreshold', 'settings.idleThresholdHint', 'settings.unitMinutes', { min: 1, disabled: !idleOn })}
        {toggle(KEYS.penaltyEnabled, 'settings.penaltyEnabled', 'settings.penaltyEnabledHint')}
        {number(KEYS.penalty, 'settings.penaltyThreshold', 'settings.penaltyThresholdHint', 'settings.unitMinutes', { min: 1, disabled: !penaltyOn })}
        {number(KEYS.days, 'settings.penaltyDays', null, 'settings.daysOfSalary', { step: 0.25, disabled: !penaltyOn })}
        {number(KEYS.max, 'settings.penaltyMax', 'settings.penaltyMaxHint', 'settings.daysOfSalary', { step: 0.25, disabled: !penaltyOn })}
        {toggle(KEYS.approval, 'settings.penaltyApproval', 'settings.penaltyApprovalHint')}
      </Section>
    </Card>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-5 border-t hairline pt-4 first-of-type:mt-0 first-of-type:border-0 first-of-type:pt-0">
      <h4 className="mb-3 text-overline uppercase text-[var(--text-tertiary)]">{title}</h4>
      <div className="grid gap-5 sm:grid-cols-2">{children}</div>
    </section>
  );
}
